/*
 * Third-party reservation/gift-card providers are driven by page globals (`FT`,
 * `Gifty`, `window._gstpln`) or by hidden DOM triggers their SDKs listen for. Those
 * SDKs live on the customer's page, NOT inside the widget iframe - so these openers
 * MUST run in the host-page context. In production the framed widget forwards the
 * intent to the host shell (see bridge `OPEN_RESERVATION`); on the local dev page the
 * widget is mounted inline, so it calls this directly. Same code, both contexts.
 */
/* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, id-length */
import { buildMessage, FRAME_TO_HOST, isFramedContext } from '@/frame/bridge';
import type { WidgetPartTyping } from '@/typings/widget';

declare const FT: { widgets: { get: () => { open: () => void } } };
declare const Gifty: { open: () => void };

declare global {
    interface Window {
        _gstpln: unknown;
    }
}

// eslint-disable-next-line max-statements
const createAndTriggerElement = <T extends HTMLElement>(
    tag: string,
    options: Partial<T> & { id: string; className: string; [key: string]: unknown },
    clickDelay = 100,
): void => {
    let trigger = document.getElementById(options.id) as T | null;
    if (!trigger) {
        trigger = document.createElement(tag) as T;

        // Exclude dataset from Object.assign since it's read-only
        const { dataset, ...assignableOptions } = options;
        Object.assign(trigger, assignableOptions);
        if (dataset) {
            Object.entries(dataset)
                .forEach(([k, v]) => {
                    if (trigger && typeof k === 'string' && typeof v === 'string') {
                        trigger.dataset[k] = v;
                    }
                });
        }
        trigger.style.display = 'none';
        document.body.appendChild(trigger);
    }
    if (trigger) {
        setTimeout(() => { trigger?.click() }, clickDelay);
    }
};

const providerOpeners: Record<number, () => void> = {
    1: () => {
        createAndTriggerElement<HTMLAnchorElement>('a', {
            id: 'zenchef-dynamic-trigger',
            href: '#',
            className: 'zc-widget-button',
            dataset: { zcAction: 'open' },
        });
    },
    2: () => {
        if (
            typeof window._gstpln === 'object'
            && window._gstpln !== null
            && typeof (window._gstpln as { openWidget?: () => void }).openWidget === 'function'
        ) {
            (window._gstpln as { openWidget: () => void }).openWidget();
        }
    },
    3: () => {
        // eslint-disable-next-line newline-per-chained-call
        FT.widgets.get().open();
    },
    4: () => {
        createAndTriggerElement<HTMLButtonElement>('button', {
            id: 'mews-dynamic-trigger',
            className: 'distributor',
        });
    },
    6: () => {
        Gifty.open();
    },
};

// Open the third-party provider for the given partner id, in the current page context.
export const openReservationProvider = (partnerId: number): void => {
    providerOpeners[partnerId]?.();
};

// Open a widget part's reservation provider, handling both plain URLs and framed contexts.
export const openWidgetPartReservation = (widgetPart: WidgetPartTyping): void => {
    const partnerId = Number(widgetPart.partnerId);

    // Plain URL: opens fine from inside the iframe (user-gesture popup).
    if (partnerId === 5) {
        window.open(String(widgetPart.value), '_blank');
        return;
    }

    // SDK-based providers live on the host page. Framed -> forward to the host shell;
    // dev inline -> run directly (same page context).
    if (isFramedContext()) {
        window.parent?.postMessage(buildMessage(FRAME_TO_HOST.OPEN_RESERVATION, { partnerId }), '*');
    } else {
        openReservationProvider(partnerId);
    }
};
